import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  TextField,
  IconButton,
  Typography,
  Avatar,
  Paper,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Divider,
} from '@mui/material';
import { Send as SendIcon } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { format } from 'date-fns';
import {
  collection,
  query,
  orderBy,
  onSnapshot,
  addDoc,
  serverTimestamp,
} from 'firebase/firestore';
import { db } from '../../firebaseConfig';
import { useAuth } from '../../contexts/AuthContext';

const TicketChat = ({ ticket }) => {
  const { t } = useTranslation();
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const messagesEndRef = useRef(null);
  const { currentUser } = useAuth();

  useEffect(() => {
    if (ticket) {
      const messagesRef = collection(db, 'tickets', ticket.id, 'messages');
      const q = query(messagesRef, orderBy('timestamp', 'asc'));

      const unsubscribe = onSnapshot(q, (snapshot) => {
        const messagesData = snapshot.docs.map((doc) => {
          const data = doc.data();
          return {
            id: doc.id,
            ...data,
            timestamp: data.timestamp?.toDate?.() || new Date(),
          };
        });
        setMessages(messagesData);
      });

      return () => unsubscribe();
    }
  }, [ticket]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const handleSendMessage = async (e) => {
    e.preventDefault();
    if (!newMessage.trim() || !ticket || !currentUser) return;

    try {
      const messagesRef = collection(db, 'tickets', ticket.id, 'messages');
      await addDoc(messagesRef, {
        content: newMessage,
        userId: currentUser.uid,
        userEmail: currentUser.email,
        userName: currentUser.displayName || currentUser.email.split('@')[0],
        timestamp: serverTimestamp(),
      });
      setNewMessage('');
    } catch (error) {
      console.error('Error sending message:', error);
    }
  };

  const formatTimestamp = (timestamp) => {
    try {
      return format(timestamp, 'MMM d, yyyy HH:mm');
    } catch (error) {
      console.error('Error formatting timestamp:', error);
      return '';
    }
  };

  return (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      <Box sx={{ flexGrow: 1, overflow: 'auto', mb: 2 }}>
        <List>
          {messages.map((message) => (
            <React.Fragment key={message.id}>
              <ListItem
                alignItems="flex-start"
                sx={{
                  flexDirection:
                    message.userId === currentUser?.uid ? 'row-reverse' : 'row',
                }}
              >
                <ListItemAvatar>
                  <Avatar>
                    {message.userName?.toUpperCase()?.charAt(0) ||
                      message.userEmail?.toUpperCase()?.charAt(0)}
                  </Avatar>
                </ListItemAvatar>
                <Box
                  sx={{
                    maxWidth: '70%',
                    ml: message.userId === currentUser?.uid ? 0 : 1,
                    mr: message.userId === currentUser?.uid ? 1 : 0,
                  }}
                >
                  <Paper
                    elevation={1}
                    sx={{
                      p: 2,
                      backgroundColor:
                        message.userId === currentUser?.uid
                          ? 'primary.main'
                          : 'background.paper',
                      color:
                        message.userId === currentUser?.uid
                          ? 'white'
                          : 'text.primary',
                    }}
                  >
                    <Typography variant="body1">{message.content}</Typography>
                    <Typography
                      variant="caption"
                      sx={{
                        display: 'block',
                        mt: 0,
                        color:
                          message.userId === currentUser?.uid
                            ? 'rgba(255, 255, 255, 0.7)'
                            : 'text.secondary',
                      }}
                    >
                      {message.userName || message.userEmail} •{' '}
                      {formatTimestamp(message.timestamp)}
                    </Typography>
                  </Paper>
                </Box>
              </ListItem>
            </React.Fragment>
          ))}
          <div ref={messagesEndRef} />
        </List>
      </Box>
      <Box
        component="form"
        onSubmit={handleSendMessage}
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          p: 1,
          borderColor: 'divider',
          flexGrow: 0.25,
        }}
      >
        <TextField
          fullWidth
          variant="outlined"
          placeholder={t('tickets.addComment')}
          value={newMessage}
          onChange={(e) => setNewMessage(e.target.value)}
          size="small"
        />
        <IconButton
          type="submit"
          color="primary"
          disabled={!newMessage.trim() || !currentUser}
        >
          <SendIcon />
        </IconButton>
      </Box>
    </Box>
  );
};

export default TicketChat;
